import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import type { Task } from '../types';
import { TASK_BAR_HEIGHT, MIN_TASK_DURATION_DAYS } from '../constants';
import { diffDays } from '../dateUtils';
import { useGanttContext } from '../GanttContext';
import { useTaskDrag } from '../hooks/useTaskDrag';
import { TaskConnectionPoints } from './TaskConnectionPoints';

type Props = {
  task: Task;
  rowId: string;
  hasUnresolvedDependencies?: boolean; // <-- Новый проп
  moveTaskDates: (taskId: string, startDate: string, endDate: string) => void;
  moveTaskToRow: (taskId: string, targetRowId: string) => void;
  createRow: () => { id: string };
  onPointClick: (taskId: string, side: 'start' | 'end', cx: number, cy: number) => void;
  onEditTask: (taskId: string) => void;
};

const ROW_HEIGHT = 36;

export const TaskBar: React.FC<Props> = ({
  task,
  rowId,
  hasUnresolvedDependencies = false, // По умолчанию false
  moveTaskDates,
  moveTaskToRow,
  createRow,
  onPointClick,
  onEditTask,
}) => {
  const { timelineStart, columnWidth, filter } = useGanttContext();

  const leftPx = useMemo(() => {
    const offsetDays = diffDays(timelineStart, task.startDate);
    return Math.max(0, offsetDays * columnWidth);
  }, [columnWidth, task.startDate, timelineStart]);

  const widthPx = useMemo(() => {
    const d = diffDays(task.startDate, task.endDate);
    return Math.max(columnWidth, Math.max(MIN_TASK_DURATION_DAYS, d) * columnWidth);
  }, [columnWidth, task.endDate, task.startDate]);

  const { bind, cursor, isDragging } = useTaskDrag({
    task,
    rowId,
    timelineStart,
    columnWidth,
    moveTaskDates,
    moveTaskToRow,
    createRow,
  });

  // Логика цвета:
  // 1. Зеленый, если задача решена
  // 2. Красный, если не решена и есть нерешенные зависимости
  // 3. Синий (по умолчанию)
  const backgroundColor = useMemo(() => {
    if (task.isResolved) return '#22c55e'; // Green
    if (hasUnresolvedDependencies) return '#ef4444'; // Red
    return '#3b82f6'; // Blue
  }, [task.isResolved, hasUnresolvedDependencies]);

  return (
    <TaskBarGroupItem
      className="task-bar-group"
      style={{
        position: 'absolute',
        left: leftPx,
        top: Math.floor((ROW_HEIGHT - TASK_BAR_HEIGHT) / 2),
        width: widthPx,
        height: TASK_BAR_HEIGHT,
        zIndex: isDragging ? 20 : 10,
      }}
    >
      {/* Полоска задачи */}
      <div
        {...bind}
        onDoubleClick={() => onEditTask(task.id)}
        style={{
          width: '100%',
          height: '100%',
          background: backgroundColor, // <-- Применяем цвет здесь
          borderRadius: 6,
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          color: 'white',
          fontSize: 11,
          lineHeight: `${TASK_BAR_HEIGHT}px`,
          padding: '0 8px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          userSelect: 'none',
          cursor: cursor ?? (isDragging ? 'grabbing' : 'grab'),
          transition: 'background-color 0.2s', // Плавная смена цвета
          opacity: !filter || task.title?.toLowerCase()?.includes(filter) ? 1 : 0.35,
        }}
        title={`${task.title} (${task.startDate} → ${task.endDate})\nDouble-click to edit`}
      >
        {task.title}
      </div>

      {/* Точки коннекторов — видны только при ховере */}
      <div className="connection-points-wrapper">
        <TaskConnectionPoints taskId={task.id} width={widthPx} onPointClick={onPointClick} />
      </div>

    </TaskBarGroupItem>
  );
};

const TaskBarGroupItem = styled.div`
  .connection-points-wrapper {
    opacity: 0;
    transition: opacity 0.15s;
  }
  &:hover {
    .connection-points-wrapper {
      opacity: 1;
    }
  }
`;
